import { useState } from "react";

const manrope = "'Manrope', sans-serif";
const cormorant = "'Cormorant Garamond', serif";

function fmt$(n) { return "$" + Number(n || 0).toLocaleString(); }
function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d + (d.length === 10 ? "T00:00:00" : ""));
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtStatus(s) {
  return (s || "open").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

// ── KPI metric bar ────────────────────────────────────────────────────────────
function KpiCell({ label, value, sub, progress, progressColor }) {
  return (
    <div style={{ flex: 1, background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, padding: "12px 16px" }}>
      <div style={{ fontSize: 8, fontFamily: manrope, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9B9690", fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 15, color: "#2B2B2B", lineHeight: 1, marginBottom: 6 }}>{value}</div>
      {progress != null && (
        <div style={{ height: 3, background: "#F0EDE6", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, progress))}%`, background: progressColor || "#7FB98E", borderRadius: 999, transition: "width 0.3s ease" }} />
        </div>
      )}
      {sub && <div style={{ fontSize: 9, color: "#9B9690", fontFamily: manrope, marginTop: progress != null ? 4 : 0 }}>{sub}</div>}
    </div>
  );
}

// ── Compact stat card ─────────────────────────────────────────────────────────
function StatCard({ label, value, accent, sub, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{ flex: 1, minWidth: 120, background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, borderTop: `2px solid ${accent}`, padding: "10px 14px 10px", cursor: onClick ? "pointer" : "default" }}
      onMouseEnter={e => onClick && (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)")}
      onMouseLeave={e => onClick && (e.currentTarget.style.boxShadow = "none")}
    >
      <div style={{ fontSize: 8, fontFamily: manrope, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9B9690", fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 18, color: "#2B2B2B", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: "#9B9690", fontFamily: manrope, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
function Badge({ status }) {
  const cfg = {
    paid:        { bg: "#EAF2ED", color: "#163C2F" },
    pending:     { bg: "#FBF3E8", color: "#B08D57" },
    overdue:     { bg: "#FEE2E2", color: "#dc2626" },
    resolved:    { bg: "#EAF2ED", color: "#163C2F" },
    open:        { bg: "#FEE2E2", color: "#dc2626" },
    in_progress: { bg: "#FBF3E8", color: "#B08D57" },
  };
  const c = cfg[(status || "").toLowerCase()] || { bg: "#F0EDE6", color: "#9B9690" };
  return (
    <span style={{ background: c.bg, color: c.color, fontSize: 9, fontFamily: manrope, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", borderRadius: 3, padding: "2px 6px", whiteSpace: "nowrap" }}>
      {fmtStatus(status)}
    </span>
  );
}

// ── Table header ──────────────────────────────────────────────────────────────
const TH = ({ children, right }) => (
  <th style={{ background: "#F9F7F4", fontFamily: manrope, fontSize: 8, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9B9690", fontWeight: 700, padding: "7px 10px", textAlign: right ? "right" : "left", border: "none", borderBottom: "0.5px solid #E8E4DC", whiteSpace: "nowrap" }}>
    {children}
  </th>
);
const TD = ({ children, right, muted, bold }) => (
  <td style={{ padding: "8px 10px", fontSize: 11, fontFamily: manrope, color: muted ? "#9B9690" : "#2B2B2B", fontWeight: bold ? 600 : 400, textAlign: right ? "right" : "left", borderBottom: "0.5px solid #F5F2EE", whiteSpace: "nowrap" }}>
    {children}
  </td>
);

// ── Activity dot ──────────────────────────────────────────────────────────────
function Dot({ type, status }) {
  const color =
    type === "payment"
      ? status === "paid" ? "#7FB98E" : status === "overdue" ? "#dc2626" : "#B08D57"
      : status === "resolved" ? "#7FB98E" : "#dc2626";
  return <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0, marginTop: 2 }} />;
}

// ── Revenue sparkline ─────────────────────────────────────────────────────────
function Sparkline({ payments }) {
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("en-US", { month: "short" }) };
  });
  const data = months.map(m => ({
    ...m,
    v: payments.filter(p => p.status === "paid" && (p.paid || p.due || "").slice(0, 7) === m.key).reduce((s, p) => s + Number(p.amount || 0), 0),
  }));
  const W = 360, H = 80, pL = 8, pR = 8, pT = 8, pB = 20;
  const plotW = W - pL - pR, plotH = H - pT - pB;
  const maxV = Math.max(...data.map(d => d.v), 500);
  const pts = data.map((d, i) => [pL + (i / (data.length - 1)) * plotW, pT + plotH - (d.v / maxV) * plotH]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const fill = `${line} L${pts[pts.length - 1][0].toFixed(1)},${(pT + plotH).toFixed(1)} L${pts[0][0].toFixed(1)},${(pT + plotH).toFixed(1)} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }}>
      <defs>
        <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7FB98E" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#7FB98E" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill="url(#sg)" />
      <path d={line} fill="none" stroke="#7FB98E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r="3" fill="#7FB98E" />)}
      {months.map((m, i) => <text key={i} x={pts[i][0].toFixed(1)} y={H - 3} textAnchor="middle" fontSize="8" fill="#9B9690" fontFamily="Manrope,sans-serif">{m.label}</text>)}
    </svg>
  );
}

// ── Tenant quick-view modal ───────────────────────────────────────────────────
function TenantModal({ payment, tenants, payments, onClose, onViewProfile, onMarkPaid, onMarkUnpaid }) {
  const tenant  = tenants.find(t => t.name === payment.tenant) || {};
  const history = payments
    .filter(p => p.tenant === payment.tenant)
    .sort((a, b) => (b.due || b.paid || "").localeCompare(a.due || a.paid || ""))
    .slice(0, 6);

  const sc = { paid: "#163C2F", overdue: "#dc2626", unpaid: "#B08D57", pending: "#B08D57" };
  const statusColor = s => sc[s] || "#9B9690";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#FFFFFF", border: "1px solid #E8E4DC", borderRadius: 14, width: "100%", maxWidth: 460, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.18)", fontFamily: manrope }}>

        {/* Header */}
        <div style={{ background: "#163C2F", padding: "20px 24px 18px", borderRadius: "14px 14px 0 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: cormorant, fontSize: 22, fontWeight: 700, color: "#F5F1EA", lineHeight: 1.1 }}>{payment.tenant}</div>
            <div style={{ fontSize: 12, color: "rgba(245,241,234,0.6)", marginTop: 4 }}>{tenant.address || payment.property || "—"}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(245,241,234,0.55)", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
        </div>

        {/* Summary strip */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: "1px solid #F5F1EA" }}>
          {[
            { label: "Monthly Rent", value: tenant.rent ? fmt$(tenant.rent) : "—" },
            { label: "Due",          value: payment.due ? fmtDate(payment.due) : "—" },
            { label: "Status",       value: payment.status },
          ].map(({ label, value }) => (
            <div key={label} style={{ padding: "14px 18px", borderRight: "1px solid #F5F1EA" }}>
              <div style={{ fontSize: 9, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: label === "Status" ? statusColor(value) : "#2B2B2B", textTransform: label === "Status" ? "capitalize" : "none" }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #F5F1EA", display: "flex", gap: 8 }}>
          {payment.status !== "paid" && (
            <button onClick={() => onMarkPaid(payment)}
              style={{ flex: 1, padding: "9px 0", background: "#163C2F", color: "#F5F1EA", border: "none", borderRadius: 8, fontFamily: manrope, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ✓ Mark Paid
            </button>
          )}
          {payment.status === "paid" && (
            <button onClick={() => onMarkUnpaid(payment)}
              style={{ flex: 1, padding: "9px 0", background: "#fff", color: "#B08D57", border: "1.5px solid #B08D57", borderRadius: 8, fontFamily: manrope, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ↩ Mark Unpaid
            </button>
          )}
        </div>

        {/* Payment history */}
        <div style={{ padding: "14px 18px 4px" }}>
          <div style={{ fontSize: 9, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 10 }}>Payment History</div>
          {history.length === 0 ? (
            <div style={{ fontSize: 12, color: "#9B9690", padding: "8px 0 10px", fontStyle: "italic" }}>No payment records found.</div>
          ) : history.map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < history.length - 1 ? "1px solid #F5F1EA" : "none" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#2B2B2B" }}>{fmt$(p.amount)}</div>
                <div style={{ fontSize: 11, color: "#9B9690", marginTop: 2 }}>{fmtDate(p.due)}{p.paid ? ` · paid ${fmtDate(p.paid)}` : ""}</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, background: statusColor(p.status) + "18", color: statusColor(p.status), border: `1px solid ${statusColor(p.status)}33`, padding: "2px 9px", borderRadius: 999, textTransform: "uppercase", letterSpacing: "0.04em" }}>{p.status}</span>
            </div>
          ))}
        </div>

        {/* View full profile */}
        <div style={{ padding: "14px 18px 18px" }}>
          <button onClick={onViewProfile}
            style={{ width: "100%", padding: "11px 0", background: "#F5F1EA", color: "#163C2F", border: "1.5px solid #163C2F", borderRadius: 8, fontFamily: manrope, fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: "0.04em" }}>
            View Full Profile →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sub-view: Rent Roll ───────────────────────────────────────────────────────
function RentRoll({ payments, tenants, selectedPeriod, activePropFilter, activeStatusFilter, filterSearch, onRowClick }) {
  const rows = payments.filter(p => {
    const dateKey = (p.paid || p.due || "").slice(0, 7);
    if (selectedPeriod && dateKey && dateKey !== selectedPeriod) return false;
    if (activePropFilter && activePropFilter !== "All" && p.property !== activePropFilter) return false;
    if (activeStatusFilter && activeStatusFilter !== "All") {
      const sl = activeStatusFilter.toLowerCase();
      if (sl === "unpaid" && p.status !== "pending") return false;
      if (sl !== "unpaid" && p.status !== sl) return false;
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (!(p.tenant || "").toLowerCase().includes(q) && !(p.property || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px 10px", borderBottom: "0.5px solid #E8E4DC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 16, color: "#2B2B2B" }}>Rent Roll</span>
        <span style={{ fontSize: 10, color: "#9B9690", fontFamily: manrope }}>{rows.length} entries</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>Tenant</TH>
              <TH>Property</TH>
              <TH>Due</TH>
              <TH right>Amount</TH>
              <TH>Status</TH>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ padding: "20px", textAlign: "center", color: "#9B9690", fontFamily: manrope, fontSize: 12 }}>No payments for this period</td></tr>
            )}
            {rows.map((p, i) => (
              <tr key={p.id || i}
                onClick={() => onRowClick?.(p)}
                style={{ cursor: onRowClick ? "pointer" : "default" }}
                onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = "#F9F7F4"; }}
                onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
                <TD bold>{p.tenant}</TD>
                <TD muted>{p.property || "—"}</TD>
                <TD muted>{fmtDate(p.due)}</TD>
                <TD right bold>{fmt$(p.amount)}</TD>
                <TD><Badge status={p.status} /></TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Sub-view: Maintenance list ────────────────────────────────────────────────
function MaintenanceList({ maintenance, activePropFilter, activeStatusFilter, filterSearch }) {
  const rows = maintenance.filter(m => {
    if (activePropFilter && activePropFilter !== "All" && m.property !== activePropFilter) return false;
    if (activeStatusFilter && activeStatusFilter !== "All") {
      const sl = activeStatusFilter.toLowerCase();
      if (sl === "paid" || sl === "unpaid" || sl === "overdue") return true;
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (!(m.issue || "").toLowerCase().includes(q) && !(m.property || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px 10px", borderBottom: "0.5px solid #E8E4DC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 16, color: "#2B2B2B" }}>Maintenance Tickets</span>
        <span style={{ fontSize: 10, color: "#9B9690", fontFamily: manrope }}>{rows.length} tickets</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>Issue</TH>
              <TH>Property</TH>
              <TH>Reported</TH>
              <TH>Priority</TH>
              <TH>Status</TH>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ padding: "20px", textAlign: "center", color: "#9B9690", fontFamily: manrope, fontSize: 12 }}>No tickets</td></tr>
            )}
            {rows.map((m, i) => (
              <tr key={m.id || i}>
                <TD bold>{m.issue}</TD>
                <TD muted>{m.property || "—"}</TD>
                <TD muted>{fmtDate(m.date_reported)}</TD>
                <TD>
                  {m.priority === "high" && (
                    <span style={{ background: "#FEE2E2", color: "#dc2626", fontSize: 9, fontFamily: manrope, fontWeight: 700, borderRadius: 3, padding: "2px 6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>High</span>
                  )}
                </TD>
                <TD><Badge status={m.status} /></TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Sub-view: Financials summary ──────────────────────────────────────────────
function FinancialsSummary({ payments, expenses, selectedPeriod }) {
  const periodPay = payments.filter(p => !selectedPeriod || (p.paid || p.due || "").slice(0, 7) === selectedPeriod);
  const periodExp = expenses.filter(e => !selectedPeriod || (e.date || "").slice(0, 7) === selectedPeriod);
  const income = periodPay.filter(p => p.status === "paid").reduce((s, p) => s + Number(p.amount || 0), 0);
  const expTotal = periodExp.reduce((s, e) => s + Number(e.amount || 0), 0);
  const net = income - expTotal;

  const byCategory = periodExp.reduce((acc, e) => {
    const k = e.category || "Uncategorized";
    acc[k] = (acc[k] || 0) + Number(e.amount || 0);
    return acc;
  }, {});

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, padding: "16px 20px" }}>
        <div style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 16, color: "#2B2B2B", marginBottom: 14 }}>Period Summary</div>
        {[
          { label: "Collected", value: fmt$(income), color: "#163C2F" },
          { label: "Expenses",  value: fmt$(expTotal), color: "#B08D57" },
          { label: "Net",       value: fmt$(net), color: net >= 0 ? "#163C2F" : "#dc2626" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid #F0EDE6" }}>
            <span style={{ fontSize: 11, fontFamily: manrope, color: "#9B9690" }}>{label}</span>
            <span style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 15, color }}>{value}</span>
          </div>
        ))}
      </div>
      <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, padding: "16px 20px" }}>
        <div style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 16, color: "#2B2B2B", marginBottom: 14 }}>Expenses by Category</div>
        {Object.keys(byCategory).length === 0 && (
          <div style={{ color: "#9B9690", fontSize: 11, fontFamily: manrope }}>No expenses this period</div>
        )}
        {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid #F0EDE6" }}>
            <span style={{ fontSize: 11, fontFamily: manrope, color: "#2B2B2B" }}>{k}</span>
            <span style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 15, color: "#B08D57" }}>{fmt$(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Dashboard({
  can,
  setShowAddPayment,
  setShowAddTenant,
  expiringLeases = [],
  rentIncreases = [],
  tenants = [],
  payments = [],
  maintenance = [],
  expenses = [],
  properties = [],
  totalRent = 0,
  collected = 0,
  overdueAmt = 0,
  openTickets = 0,
  totalExpenses = 0,
  setPage,
  setPropTab,
  setViewMaint,
  setSelectedTenant,
  changePaymentStatus,
  subTab = "Overview",
  setSubTab,
  selectedPeriod,
  dashPeriod: dashPeriodProp,
  setDashPeriod: setDashPeriodProp,
  activePropFilter,
  activeStatusFilter,
  filterSearch,
}) {
  const now     = new Date();
  const thisYear = now.getFullYear();
  const thisMonthKey = `${thisYear}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const lastMonthDate = new Date(thisYear, now.getMonth() - 1, 1);
  const lastMonthKey  = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, "0")}`;

  // ── Period selector (lifted to AppShell for global sync) ─────────────────
  const dashPeriod    = dashPeriodProp    ?? "month";
  const setDashPeriod = setDashPeriodProp ?? (() => {});

  // Parse the AppShell month picker value (YYYY-MM) so the KPI stats always
  // reflect the selected month, not just "now".
  const [selYear, selMonthNum] = (selectedPeriod || thisMonthKey)
    .split("-").map(Number);                              // e.g. [2026, 5]

  const periodStart = (() => {
    if (dashPeriod === "annual")  return new Date(selYear, 0, 1);
    if (dashPeriod === "3month")  return new Date(selYear, selMonthNum - 3, 1);
    return new Date(selYear, selMonthNum - 1, 1);         // just the selected month
  })();

  const periodEnd = (() => {
    if (dashPeriod === "annual")  return new Date(selYear + 1, 0, 1);
    if (dashPeriod === "3month")  return new Date(selYear, selMonthNum, 1);
    return new Date(selYear, selMonthNum, 1);              // exclusive: start of next month
  })();

  const inPeriod = dateStr => {
    if (!dateStr) return false;
    const d = new Date(dateStr + "T00:00:00");
    return d >= periodStart && d < periodEnd;
  };

  // Payments collected in the selected period (use paid date, fall back to due)
  const periodPaidPayments = payments.filter(p => p.status === "paid" && inPeriod(p.paid || p.due));
  const collectedPeriod    = periodPaidPayments.reduce((s, p) => s + Number(p.amount || 0), 0);

  // Expenses in the selected period
  const expensesPeriod  = expenses.filter(e => inPeriod(e.date)).reduce((s, e) => s + Number(e.amount || 0), 0);

  // Expected rent for the period (monthly totalRent × number of months)
  const monthsInPeriod = dashPeriod === "annual" ? 12 : dashPeriod === "3month" ? 3 : 1;
  const expectedPeriod = totalRent * monthsInPeriod;

  // Collection rate — always period-scoped, never exceeds 100% for a sane period
  const collectionRate = expectedPeriod > 0 ? Math.min(999, Math.round((collectedPeriod / expectedPeriod) * 100)) : 0;

  const netProfit      = collectedPeriod - expensesPeriod;

  // Expense delta badge: compare selected month vs the month before it
  const selMonthKey  = selectedPeriod || thisMonthKey;
  const prevMonthDate = new Date(selYear, selMonthNum - 2, 1);
  const prevMonthKey  = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, "0")}`;
  const thisMonthExp = expenses.filter(e => (e.date || "").slice(0, 7) === selMonthKey).reduce((s, e) => s + Number(e.amount || 0), 0);
  const lastMonthExp = expenses.filter(e => (e.date || "").slice(0, 7) === prevMonthKey).reduce((s, e) => s + Number(e.amount || 0), 0);
  const expDelta = thisMonthExp - lastMonthExp;

  // Active = status is "active" or "month-to-month", OR status not set,
  // OR lease has no end date (month-to-month with null lease_end).
  const activeTenants = tenants.filter(t => {
    const s = t.status || "active";
    if (s === "active" || s === "month-to-month") return true;
    // Treat a missing/null lease end date as active (month-to-month)
    if (!t.lease_end) return true;
    return false;
  }).length;
  const urgentCount   = maintenance.filter(m => m.status !== "resolved" && m.priority === "high").length;

  // "YTD / Period Income" label adapts to the selector
  const periodLabel = dashPeriod === "annual" ? String(thisYear)
    : dashPeriod === "3month" ? "Last 3 months"
    : "This month";

  const activityItems = [
    ...payments.slice(0, 6).map(p => ({ type: "payment", title: p.tenant, sub: `${fmt$(p.amount)}${p.property ? " · " + p.property : ""}`, date: p.paid || p.due || "", status: p.status })),
    ...maintenance.filter(m => m.status !== "resolved").slice(0, 3).map(m => ({ type: "maintenance", title: m.issue, sub: m.property || "", date: m.date_reported || "", status: m.status })),
  ].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 7);

  const openMaint = maintenance.filter(m => m.status !== "resolved").slice(0, 6);

  // ── Tenant quick-view modal ─────────────────────────────────────────────
  const [tenantModal, setTenantModal] = useState(null); // payment row being inspected

  const openTenantModal = p => setTenantModal(p);

  const handleModalMarkPaid = async p => {
    await changePaymentStatus?.(p.id, p.status, "paid",   "Marked paid from dashboard");
    setTenantModal(null);
  };
  const handleModalMarkUnpaid = async p => {
    await changePaymentStatus?.(p.id, p.status, "unpaid", "Marked unpaid from dashboard");
    setTenantModal(null);
  };
  const handleViewProfile = () => {
    const t = tenants.find(t => t.name === tenantModal?.tenant);
    setTenantModal(null);
    if (t && setSelectedTenant) { setSelectedTenant(t); setPage("Tenants"); }
    else setPage("Tenants");
  };

  // Expiring leases
  const today60 = new Date(); today60.setDate(today60.getDate() + 60);
  const upcomingIncreases = rentIncreases.filter(ri =>
    !ri.applied &&
    ri.effective_date >= now.toISOString().slice(0, 10) &&
    new Date(ri.effective_date) <= today60
  ).sort((a, b) => a.effective_date.localeCompare(b.effective_date));

  return (
    <>
      {/* Tenant quick-view modal */}
      {tenantModal && (
        <TenantModal
          payment={tenantModal}
          tenants={tenants}
          payments={payments}
          onClose={() => setTenantModal(null)}
          onViewProfile={handleViewProfile}
          onMarkPaid={handleModalMarkPaid}
          onMarkUnpaid={handleModalMarkUnpaid}
        />
      )}

      {/* Expiring leases */}
      {expiringLeases.length > 0 && (
        <div style={{ background: "#FBF3E8", border: "0.5px solid #B08D5744", borderRadius: 5, padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: "#B08D57", marginBottom: 6, fontSize: 9, fontFamily: manrope, textTransform: "uppercase", letterSpacing: "0.08em" }}>Leases Expiring Within 60 Days</div>
          {expiringLeases.map((t, i) => (
            <div key={i} style={{ fontSize: 12, color: "#2B2B2B", marginBottom: 3, fontFamily: manrope }}>
              <strong>{t.name}</strong> · {t.address} · Expires <strong>{t.lease_end}</strong>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming rent increases */}
      {upcomingIncreases.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: "#163C2F", marginBottom: 8, fontSize: 9, fontFamily: manrope, textTransform: "uppercase", letterSpacing: "0.08em" }}>Upcoming Rent Increases — 60 Days</div>
          {upcomingIncreases.map((ri, i) => {
            const tenant = tenants.find(t => t.name === ri.tenant);
            return (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#2B2B2B", padding: "6px 0", borderBottom: i < upcomingIncreases.length - 1 ? "0.5px solid #F0EDE6" : "none" }}>
                <span style={{ fontFamily: manrope }}><strong>{ri.tenant}</strong>{tenant?.address && <span style={{ color: "#9B9690" }}> · {tenant.address}</span>}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ color: "#9B9690", fontSize: 11 }}>${Number(tenant?.rent || 0).toLocaleString()} → <strong style={{ color: "#163C2F" }}>${Number(ri.new_rent).toLocaleString()}</strong></span>
                  <span style={{ fontSize: 9, background: "#EAF2ED", color: "#163C2F", borderRadius: 3, padding: "2px 8px", fontWeight: 600, fontFamily: manrope }}>Eff. {ri.effective_date}</span>
                  <button onClick={() => { setPage("Properties"); setPropTab("leases"); }} style={{ fontSize: 9, padding: "3px 8px", borderRadius: 3, border: "0.5px solid #E8E4DC", background: "#fff", color: "#2B2B2B", cursor: "pointer", fontFamily: manrope }}>View</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sub-tab: Rent Roll */}
      {subTab === "Rent Roll" && (
        <RentRoll payments={payments} tenants={tenants} selectedPeriod={selectedPeriod} activePropFilter={activePropFilter} activeStatusFilter={activeStatusFilter} filterSearch={filterSearch} onRowClick={openTenantModal} />
      )}

      {/* Sub-tab: Maintenance */}
      {subTab === "Maintenance" && (
        <MaintenanceList maintenance={maintenance} activePropFilter={activePropFilter} activeStatusFilter={activeStatusFilter} filterSearch={filterSearch} />
      )}

      {/* Sub-tab: Financials */}
      {subTab === "Financials" && (
        <FinancialsSummary payments={payments} expenses={expenses} selectedPeriod={selectedPeriod} />
      )}

      {/* Sub-tab: Invoices */}
      {subTab === "Invoices" && (
        <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, padding: "32px", textAlign: "center" }}>
          <div style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 18, color: "#2B2B2B", marginBottom: 8 }}>Invoices</div>
          <div style={{ fontSize: 12, color: "#9B9690", fontFamily: manrope, marginBottom: 16 }}>Manage and send invoices from the Payments page.</div>
          <button onClick={() => setPage("Payments")} style={{ background: "#163C2F", color: "#F5F1EA", border: "none", borderRadius: 3, padding: "8px 20px", fontFamily: manrope, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Go to Payments →</button>
        </div>
      )}

      {/* Sub-tab: Overview */}
      {subTab === "Overview" && (
        <>
          {/* Period toggle + KPI bar header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontFamily: manrope, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9B9690", fontWeight: 700 }}>Financial Overview</div>
            <div style={{ display: "flex", gap: 3 }}>
              {[
                { key: "month",   label: "This Month" },
                { key: "3month",  label: "3 Months"   },
                { key: "annual",  label: String(thisYear) },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => setDashPeriod(key)} style={{
                  padding: "3px 10px", borderRadius: 4, border: `1px solid ${dashPeriod === key ? "#163C2F" : "#E8E4DC"}`,
                  background: dashPeriod === key ? "#163C2F" : "#fff",
                  color: dashPeriod === key ? "#F5F1EA" : "#9B9690",
                  fontFamily: manrope, fontSize: 10, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                }}>{label}</button>
              ))}
            </div>
          </div>

          {/* KPI Bar */}
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <KpiCell
              label={dashPeriod === "month" ? "Expected Rent" : `Expected (${monthsInPeriod}mo)`}
              value={fmt$(expectedPeriod)}
              sub={dashPeriod === "month" ? "Monthly total billed" : `${monthsInPeriod} × monthly rent`}
              progress={collectionRate}
              progressColor="#7FB98E"
            />
            <KpiCell
              label="Collected"
              value={fmt$(collectedPeriod)}
              sub={`${collectionRate}% collection rate`}
              progress={Math.min(100, collectionRate)}
              progressColor="#163C2F"
            />
            <KpiCell
              label="Expenses"
              value={fmt$(expensesPeriod)}
              sub={dashPeriod === "month" && lastMonthExp > 0
                ? (expDelta >= 0 ? `+${fmt$(expDelta)} vs last month` : `${fmt$(expDelta)} vs last month`)
                : periodLabel}
              progress={expectedPeriod > 0 ? Math.min(100, (expensesPeriod / expectedPeriod) * 100) : 0}
              progressColor="#B08D57"
            />
          </div>

          {/* Stat Cards */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <StatCard label="Net Profit" value={fmt$(netProfit)} accent={netProfit >= 0 ? "#163C2F" : "#dc2626"} sub={periodLabel} onClick={() => setPage("Financials")} />
            <StatCard label="Collection Rate" value={`${collectionRate}%`} accent="#7FB98E" sub={`${fmt$(overdueAmt)} overdue`} onClick={() => setPage("Payments")} />
            <StatCard label="Active Tenants" value={String(activeTenants)} accent="#9B9690" sub={`${tenants.length} total`} onClick={() => setPage("Tenants")} />
            <StatCard label="Open Tickets" value={String(openTickets)} accent="#dc2626" sub={urgentCount > 0 ? `${urgentCount} urgent` : "No urgent"} onClick={() => setPage("Maintenance")} />
            <StatCard label={dashPeriod === "annual" ? "Annual Income" : dashPeriod === "3month" ? "3-Month Income" : "Monthly Income"} value={fmt$(collectedPeriod)} accent="#B08D57" sub={periodLabel} />
          </div>

          {/* Two-column grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

            {/* Left: Rent Roll table */}
            <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px 8px", borderBottom: "0.5px solid #E8E4DC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 15, color: "#2B2B2B" }}>Rent Roll</span>
                <span onClick={() => setPage("Payments")} style={{ fontSize: 9, color: "#9B9690", fontFamily: manrope, textTransform: "uppercase", letterSpacing: "0.07em", cursor: "pointer" }}>All →</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr><TH>Tenant</TH><TH right>Amount</TH><TH>Status</TH></tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = payments.filter(p => {
                        const dk = (p.paid || p.due || "").slice(0, 7);
                        if (selectedPeriod && dk && dk !== selectedPeriod) return false;
                        if (activePropFilter && activePropFilter !== "All" && p.property !== activePropFilter) return false;
                        if (filterSearch) { const q = filterSearch.toLowerCase(); if (!(p.tenant || "").toLowerCase().includes(q)) return false; }
                        return true;
                      }).slice(0, 8);
                      if (filtered.length === 0) return (
                        <tr><td colSpan={3} style={{ padding: "16px", textAlign: "center", color: "#9B9690", fontFamily: manrope, fontSize: 11 }}>No payments</td></tr>
                      );
                      return filtered.map((p, i) => (
                        <tr key={p.id || i} onClick={() => openTenantModal(p)}
                          style={{ cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#F9F7F4"}
                          onMouseLeave={e => e.currentTarget.style.background = ""}>
                          <TD bold>{p.tenant}</TD>
                          <TD right>{fmt$(p.amount)}</TD>
                          <TD><Badge status={p.status} /></TD>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right: Open tickets + Activity */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Open maintenance */}
              <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px 8px", borderBottom: "0.5px solid #E8E4DC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 15, color: "#2B2B2B" }}>Open Tickets</span>
                  <span onClick={() => setPage("Maintenance")} style={{ fontSize: 9, color: "#9B9690", fontFamily: manrope, textTransform: "uppercase", letterSpacing: "0.07em", cursor: "pointer" }}>All →</span>
                </div>
                {openMaint.length === 0 && (
                  <div style={{ padding: "14px", color: "#9B9690", fontSize: 11, fontFamily: manrope, textAlign: "center" }}>No open tickets</div>
                )}
                {openMaint.map((m, i) => (
                  <div
                    key={m.id || i}
                    onClick={() => setViewMaint?.(m)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", borderBottom: i < openMaint.length - 1 ? "0.5px solid #F5F2EE" : "none", cursor: setViewMaint ? "pointer" : "default", transition: "background 0.12s" }}
                    onMouseEnter={e => { if (setViewMaint) e.currentTarget.style.background = "#F9F7F4"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, fontFamily: manrope, color: "#2B2B2B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.issue}</div>
                      <div style={{ fontSize: 9, color: "#9B9690", fontFamily: manrope, marginTop: 1 }}>{m.property || "—"} {m.tenant ? `· ${m.tenant}` : ""}</div>
                    </div>
                    <Badge status={m.status} />
                  </div>
                ))}
              </div>

              {/* Activity log */}
              <div style={{ background: "#fff", border: "0.5px solid #E8E4DC", borderRadius: 5, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px 8px", borderBottom: "0.5px solid #E8E4DC" }}>
                  <span style={{ fontFamily: cormorant, fontWeight: 600, fontSize: 15, color: "#2B2B2B" }}>Recent Activity</span>
                </div>
                {activityItems.length === 0 && (
                  <div style={{ padding: "14px", color: "#9B9690", fontSize: 11, fontFamily: manrope, textAlign: "center" }}>No activity</div>
                )}
                {activityItems.map((item, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 14px", borderBottom: idx < activityItems.length - 1 ? "0.5px solid #F5F2EE" : "none" }}>
                    <Dot type={item.type} status={item.status} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, fontFamily: manrope, color: "#2B2B2B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
                      <div style={{ fontSize: 9, color: "#9B9690", fontFamily: manrope, marginTop: 1 }}>{item.sub}</div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: "right" }}>
                      {item.date && <div style={{ fontSize: 9, color: "#9B9690", fontFamily: manrope }}>{fmtDate(item.date)}</div>}
                    </div>
                  </div>
                ))}

                {/* Revenue sparkline */}
                <div style={{ padding: "12px 14px 8px", borderTop: "0.5px solid #F0EDE6" }}>
                  <div style={{ fontSize: 8, textTransform: "uppercase", color: "#9B9690", fontFamily: manrope, letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>Revenue — 6 months</div>
                  <Sparkline payments={payments} />
                </div>
              </div>

            </div>
          </div>
        </>
      )}
    </>
  );
}
